import * as React from 'react';
import { ComplexValue } from 'app/types-shared';
import { STATE } from 'app/constants';
import { containerFactory } from 'app/infrastructure/redux/index';
import { actionCreators } from './actions';
import { Paper } from 'ui';
import { ElementLoader } from 'ui/common/element-loader/element-loader';
import { SubscriptionsProps } from './types';
import { ScrollToTop } from 'ui/scroll-to-top/scroll-to-top';
import { UserProfileHeading } from 'app/area/user-profile/user-profile-heading';
import { SubscriptionsPanel } from 'app/area/user-profile/subscriptions/subscriptions-panel/subscriptions-panel';
import { alertsFormName, responsibleForUnitFormName, subscriptionSectionId, subscriptionsFormName,
  subscriptionTranslations } from 'app/area/user-profile/subscriptions/constants';
import { UserProfileSectionHeading } from 'app/area/user-profile/user-profile-section-heading';
import { ResponsibleForUnitPanel } from 'app/area/user-profile/subscriptions/responsible-for-unit-panel/responsible-for-unit-panel';
import { UserProfileWarning } from 'app/area/user-profile/user-profile-warning';
import { activeSubscriptions } from 'app/area/user-profile/subscriptions/subscriptions-panel/mappers';
import { NoContent } from 'app/area/user-profile/no-content';

import type { JSX } from "react";

export class SubscriptionsComponent extends React.Component<SubscriptionsProps> {
  public static statePath: string = STATE.subscriptions;

  public componentDidMount() {
    this.loadSubscriptionsData();
    this.props.loadResponsobleForUnit();
    this.props.loadAlerts();
  }

  private loadSubscriptionsData = () => {
    this.props.loadSubscriptionOptions();
    this.props.loadSubscription();
  };

  private saveSubscriptions = (values: ComplexValue<number>[]) => {
    this.props.saveSubscriptions(values, this.loadSubscriptionsData);
  };

  public render(): JSX.Element {
    const { translation, fetchingLoadSubscriptions, fetchingSubscriptionOptions,
      subscriptions, subscriptionOptions, validation, fetchingSaveSubscriptions,
      fetchingLoadResponsibleForUnit, responsibleForUnit, alerts, fetchingLoadAlerts,
      sharedState: { email, locale } } = this.props;

    const activeAlerts = activeSubscriptions(alerts);
    const shouldRenderResponsibleForUnit = !fetchingLoadResponsibleForUnit && responsibleForUnit;
    const activeAlertsExist = activeAlerts.length > 0;
    const shouldRenderAlerts = !fetchingLoadAlerts && activeAlertsExist;
    const shouldRenderSubscriptions = !fetchingSaveSubscriptions && !fetchingLoadSubscriptions
      && !fetchingSubscriptionOptions && subscriptions;

    const activeSubscriptionsExist = activeSubscriptions(subscriptions)?.length > 0 || activeSubscriptions(subscriptionOptions)?.length > 0;
    const responsibleForUnitsExist = responsibleForUnit?.length > 0;

    const isNotFetching = !fetchingLoadAlerts && !fetchingSaveSubscriptions && !fetchingLoadSubscriptions && !fetchingSubscriptionOptions
      && !fetchingLoadResponsibleForUnit;

    const shouldRenderWarning = !email && !(shouldRenderAlerts && !activeSubscriptionsExist && !responsibleForUnitsExist) && isNotFetching;

    if (!activeSubscriptionsExist && !activeAlertsExist && !responsibleForUnitsExist && isNotFetching) {
      return <NoContent translation={translation} componentId={subscriptionSectionId} />;
    }

    return <Paper componentId={subscriptionSectionId} className="user-profile-section-paper">
      <UserProfileHeading title={translation.main_title} className={!email ? 'mb-0' : ''} />

      {shouldRenderWarning && <UserProfileWarning text={translation.no_email} />}
      {activeSubscriptionsExist ? <>
        <UserProfileSectionHeading
          sectionTitle={translation.subscriptions_section_title}
          disclaimer={translation.subscriptions_section_disclaimer} />
        <section>
          <div className="main-grid">
            {!shouldRenderSubscriptions
              ? <ElementLoader />
              : <SubscriptionsPanel
                formName={subscriptionsFormName}
                subscribedText={translation.subscribed_text}
                subscriptions={subscriptions}
                optionsCodes={{ fetching: fetchingSubscriptionOptions, values: subscriptionOptions }}
                validation={validation}
                locale={locale}
                translation={translation}
                saveSubscriptions={this.saveSubscriptions} />}
          </div>
        </section>
      </> : null}

      {activeAlertsExist ? <>
        <UserProfileSectionHeading
          sectionTitle={translation.alerts_title}
          disclaimer={translation.alert_disclaimer} />
        <section>
          <div className="main-grid">
            {!shouldRenderAlerts
              ? <ElementLoader />
              : <SubscriptionsPanel
                formName={alertsFormName}
                subscribedText={translation.subscribed_text}
                subscriptions={alerts}
                validation={validation}
                locale={locale}
                translation={translation} />}
          </div>
        </section>
      </> : null}

      {responsibleForUnitsExist ? <>
        <UserProfileSectionHeading
          sectionTitle={translation.unassign_cases_action_title}
          disclaimer={translation.unassign_cases_action_disclaimer} />
        <section>
          <div className="main-grid">
            {!shouldRenderResponsibleForUnit
              ? <ElementLoader />
              : <ResponsibleForUnitPanel
                formName={responsibleForUnitFormName}
                valueItems={responsibleForUnit}
                validation={validation}
                translation={translation}
              />}
          </div>
        </section>
      </> : null}
      <ScrollToTop />
    </Paper>;
  }
}

const container = containerFactory(SubscriptionsComponent, actionCreators, {}, subscriptionTranslations);

export { container as Subscriptions };
